/**
 * AddressText (ADDR-LINK, Seth 2026-06-07) — renders free text and turns any
 * ENTITY inside it that already exists in Pegasus into a link to that record:
 *   • an ADDRESS  ("…neighbor at 8428 97 1/2 Street W")  → that property's 360
 *   • a NAME      ("Paired with STB Printing")           → that customer's 360
 *
 * One component, used everywhere free text might name something we know (the
 * Good-to-Know slide-out, the Access & preferences card, …) so the linking is
 * consistent across the whole site.
 *
 * Two deliberately different matchers, both best-effort and PRECISION-FIRST —
 * a wrong link is worse than no link:
 *   - match_address: anchors on the house number, corroborated by the street
 *     token. We linkify the address span as it's WORDED IN THE TEXT (same word
 *     count as the stored address) so abbreviations don't break it — the note
 *     says "Ave", the record says "Avenue", and it still links. (This was the
 *     bug on 0051424: the old code required the stored address verbatim.)
 *   - match_name: only links a name when it hits >=2 whole-word tokens of a
 *     customer's name AND that customer is the unique top scorer — so "STB
 *     Printing" links, but the substring trap (weSTBy / oSTBy) never does, and
 *     a lone common first name ("John") is left alone.
 *
 * Address wins when both could apply (one link per text for now; multi-match in
 * a long note is ADDR-LINK-02). Never links a record to itself (selfCustno).
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

interface AddrMatch {
  custno: string;
  add1: string;
  locno: string;
}
interface NameMatch {
  custno: string;
  locno: string;
  anchor: string;
}

const RE_HOUSE_NUM = /\b\d{2,6}\b/;
// "namey" = two capitalized words, or an ALL-CAPS acronym (STB, ABC). Gates the
// name matcher so it doesn't run on every lowercase line.
const RE_NAMEY = /\b[A-Z][a-zA-Z]+\b[^.]*\b[A-Z][a-zA-Z]+\b|\b[A-Z]{2,}\b/;

function escapeRe(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function useAddressMatch(text: string) {
  return useQuery({
    queryKey: ["address_match", text],
    enabled: text.length > 6 && RE_HOUSE_NUM.test(text),
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<AddrMatch | null> => {
      const { data, error } = await supabase.rpc("match_address", { p_text: text });
      if (error) return null;
      return ((data ?? []) as AddrMatch[])[0] ?? null;
    },
  });
}

export function useNameMatch(text: string) {
  // Skip when there's a house number — that's an address candidate, and address
  // wins; this also avoids a second RPC on every address line.
  const enabled = text.length > 4 && !RE_HOUSE_NUM.test(text) && RE_NAMEY.test(text);
  return useQuery({
    queryKey: ["name_match", text],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<NameMatch | null> => {
      const { data, error } = await supabase.rpc("match_name", { p_text: text });
      if (error) return null;
      return ((data ?? []) as NameMatch[])[0] ?? null;
    },
  });
}

// Linkify the address as it's WORDED in the text: house number + the same number
// of words the stored address has (abbreviation-robust). Falls back to a verbatim
// match of the stored address. Returns the [start, end) span, or null.
function addressSpan(text: string, add1: string): [number, number] | null {
  const num = (text.match(RE_HOUSE_NUM) || [])[0];
  if (num) {
    const words = add1.trim().split(/\s+/).filter(Boolean).length || 1;
    const re = new RegExp("\\b" + escapeRe(num) + "(?:\\s+\\S+){0," + Math.max(0, words - 1) + "}");
    const m = text.match(re);
    if (m && m.index != null) {
      let end = m.index + m[0].length;
      while (end > m.index && /[^A-Za-z0-9]/.test(text[end - 1]!)) end--; // trim trailing punctuation
      return [m.index, end];
    }
  }
  const idx = text.toLowerCase().indexOf(add1.toLowerCase());
  if (idx >= 0) return [idx, idx + add1.length];
  return null;
}

// Minimal span covering every anchor token (whole-word, case-insensitive). Bails
// if any token isn't literally present, so we never linkify a phantom span.
function nameSpan(text: string, anchor: string): [number, number] | null {
  const toks = anchor.split(/\s+/).filter(Boolean);
  if (toks.length === 0) return null;
  let lo = Infinity;
  let hi = -1;
  for (const tk of toks) {
    const m = text.match(new RegExp("\\b" + escapeRe(tk) + "\\b", "i"));
    if (!m || m.index == null) return null;
    lo = Math.min(lo, m.index);
    hi = Math.max(hi, m.index + m[0].length);
  }
  return hi > lo ? [lo, hi] : null;
}

export function AddressText({
  text,
  selfCustno,
}: {
  text?: string | null;
  selfCustno?: string | null;
}) {
  const value = text ?? "";
  const { data: addr } = useAddressMatch(value);
  const { data: name } = useNameMatch(value);

  let span: [number, number] | null = null;
  let href: string | null = null;

  if (addr && addr.custno && addr.custno !== selfCustno) {
    span = addressSpan(value, addr.add1);
    if (span) href = `/customers/${addr.custno}?loc=${addr.locno}`;
  }
  if (!span && name && name.custno && name.custno !== selfCustno) {
    span = nameSpan(value, name.anchor);
    if (span) href = `/customers/${name.custno}?loc=${name.locno}`;
  }

  if (!span || !href) return <>{value}</>;
  const [s, e] = span;
  return (
    <>
      {value.slice(0, s)}
      <Link
        to={href}
        title="Open this record in Pegasus"
        onClick={(ev) => ev.stopPropagation()}
        className="font-medium text-brand-700 underline decoration-brand-300 underline-offset-2 transition-colors hover:text-brand-900"
      >
        {value.slice(s, e)}
      </Link>
      {value.slice(e)}
    </>
  );
}
